import React, { useState, useEffect } from 'react';
import db from '../services/database';
import ResultsDisplay from './ResultsDisplay';
import './AdvancedSearch.css';

function AdvancedSearch({ setSelectedMaterial }) {
    const [filters, setFilters] = useState([]);
    const [results, setResults] = useState([]);
    const [materialTypes, setMaterialTypes] = useState([]);
    const [properties, setProperties] = useState([]);
    const [applications, setApplications] = useState([]);
    const [elements, setElements] = useState([]);
    const [logicOperator, setLogicOperator] = useState('AND');

    const filterTypes = [
        { value: 'type', label: 'Тип матеріалу' },
        { value: 'density', label: 'Густина' },
        { value: 'property', label: 'Властивість' },
        { value: 'application', label: 'Застосування' },
        { value: 'element', label: 'Хімічний елемент' },
        { value: 'name', label: 'Назва матеріалу' }
    ];

    const operators = {
        numeric: [
            { value: '=', label: '=' },
            { value: '>', label: '>' },
            { value: '<', label: '<' },
            { value: '>=', label: '>=' },
            { value: '<=', label: '<=' },
            { value: 'BETWEEN', label: 'між' }
        ],
        text: [
            { value: 'LIKE', label: 'містить' },
            { value: '=', label: 'дорівнює' }
        ],
        select: [
            { value: '=', label: 'дорівнює' }
        ]
    };

    useEffect(() => {
        loadReferenceData();
    }, []);

    const loadReferenceData = () => {
        try {
            const types = db.executeQuery('SELECT * FROM MaterialTypes ORDER BY name');
            setMaterialTypes(types);

            const props = db.executeQuery('SELECT * FROM Properties ORDER BY name');
            setProperties(props);

            const apps = db.executeQuery('SELECT * FROM Applications ORDER BY name');
            setApplications(apps);

            const elems = db.executeQuery('SELECT * FROM Elements ORDER BY symbol');
            setElements(elems);
        } catch (error) {
            console.error('Error loading reference data:', error);
        }
    };

    const addFilter = () => {
        const newFilter = {
            id: Date.now(),
            type: 'type',
            operator: '=',
            value: '',
            value2: '' // For BETWEEN operator
        };
        setFilters([...filters, newFilter]);
    };

    const removeFilter = (id) => {
        setFilters(filters.filter(f => f.id !== id));
    };

    const updateFilter = (id, field, value) => {
        setFilters(filters.map(f => {
            if (f.id === id) {
                const updated = { ...f, [field]: value };
                // Reset operator when type changes
                if (field === 'type') {
                    if (value === 'density' || value === 'property') {
                        updated.operator = '=';
                    } else if (value === 'name') {
                        updated.operator = 'LIKE';
                    } else {
                        updated.operator = '=';
                    }
                    updated.value = '';
                    updated.value2 = '';
                }
                return updated;
            }
            return f;
        }));
    };

    const buildQuery = () => {
        if (filters.length === 0) {
            return 'SELECT DISTINCT m.* FROM Materials m';
        }

        let query = 'SELECT DISTINCT m.* FROM Materials m';
        const joins = new Set();
        const conditions = [];
        const params = [];

        filters.forEach((filter, index) => {
            switch (filter.type) {
                case 'type':
                    if (filter.value) {
                        conditions.push('m.type_id = ?');
                        params.push(filter.value);
                    }
                    break;

                case 'density':
                    if (filter.value) {
                        if (filter.operator === 'BETWEEN' && filter.value2) {
                            conditions.push('m.density BETWEEN ? AND ?');
                            params.push(parseFloat(filter.value), parseFloat(filter.value2));
                        } else {
                            conditions.push(`m.density ${filter.operator} ?`);
                            params.push(parseFloat(filter.value));
                        }
                    }
                    break;

                case 'property':
                    if (filter.value && filter.value2) {
                        joins.add('LEFT JOIN MaterialProperties mp ON m.id = mp.material_id');
                        const [propertyId, operator, value] = filter.value.split('|');
                        
                        if (operator === 'BETWEEN' && filter.value2) {
                            conditions.push(`(mp.property_id = ? AND mp.value BETWEEN ? AND ?)`);
                            params.push(parseInt(propertyId), parseFloat(value), parseFloat(filter.value2));
                        } else {
                            conditions.push(`(mp.property_id = ? AND mp.value ${operator} ?)`);
                            params.push(parseInt(propertyId), parseFloat(value));
                        }
                    }
                    break;

                case 'application':
                    if (filter.value) {
                        joins.add('LEFT JOIN MaterialApplications ma ON m.id = ma.material_id');
                        conditions.push('ma.application_id = ?');
                        params.push(filter.value);
                    }
                    break;

                case 'element':
                    if (filter.value) {
                        joins.add('LEFT JOIN MaterialComposition mc ON m.id = mc.material_id');
                        
                        if (filter.operator === 'BETWEEN' && filter.value2) {
                            const [elementId, minPercent] = filter.value.split('|');
                            conditions.push('(mc.element_id = ? AND mc.percentage BETWEEN ? AND ?)');
                            params.push(parseInt(elementId), parseFloat(minPercent), parseFloat(filter.value2));
                        } else {
                            conditions.push('mc.element_id = ?');
                            params.push(filter.value);
                        }
                    }
                    break;

                case 'name':
                    if (filter.value) {
                        if (filter.operator === 'LIKE') {
                            conditions.push('m.name LIKE ?');
                            params.push(`%${filter.value}%`);
                        } else {
                            conditions.push('m.name = ?');
                            params.push(filter.value);
                        }
                    }
                    break;

                default:
                    break;
            }
        });

        // Add joins
        if (joins.size > 0) {
            query += ' ' + Array.from(joins).join(' ');
        }

        // Add conditions
        if (conditions.length > 0) {
            query += ' WHERE ' + conditions.join(` ${logicOperator} `);
        }

        query += ' ORDER BY m.name';

        return { query, params };
    };

    const executeSearch = () => {
        try {
            const { query, params } = buildQuery();
            console.log('Executing query:', query, 'with params:', params);
            
            const materials = db.executeQuery(query, params);
            setResults(materials);
        } catch (error) {
            console.error('Error executing search:', error);
            alert('Помилка виконання пошуку: ' + error.message);
        }
    };

    const clearFilters = () => {
        setFilters([]);
        setResults([]);
    };

    const renderFilterValue = (filter) => {
        switch (filter.type) {
            case 'type':
                return (
                    <select
                        value={filter.value}
                        onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                        className="filter-input"
                    >
                        <option value="">Оберіть тип</option>
                        {materialTypes.map(type => (
                            <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                    </select>
                );

            case 'density':
                return (
                    <>
                        <input
                            type="number"
                            value={filter.value}
                            onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                            placeholder="Значення"
                            className="filter-input"
                            step="0.01"
                        />
                        {filter.operator === 'BETWEEN' && (
                            <input
                                type="number"
                                value={filter.value2}
                                onChange={(e) => updateFilter(filter.id, 'value2', e.target.value)}
                                placeholder="До"
                                className="filter-input"
                                step="0.01"
                            />
                        )}
                    </>
                );

            case 'property':
                return (
                    <>
                        <select
                            value={filter.value}
                            onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                            className="filter-input"
                        >
                            <option value="">Оберіть властивість</option>
                            {properties.map(prop => (
                                <optgroup key={prop.id} label={prop.name}>
                                    <option value={`${prop.id}|>|0`}>{prop.name} &gt; (введіть значення)</option>
                                    <option value={`${prop.id}|<|999999`}>{prop.name} &lt; (введіть значення)</option>
                                    <option value={`${prop.id}|=|0`}>{prop.name} = (введіть значення)</option>
                                </optgroup>
                            ))}
                        </select>
                        {filter.value && (
                            <>
                                <input
                                    type="number"
                                    value={filter.value.split('|')[2] || ''}
                                    onChange={(e) => {
                                        const parts = filter.value.split('|');
                                        parts[2] = e.target.value;
                                        updateFilter(filter.id, 'value', parts.join('|'));
                                    }}
                                    placeholder="Значення"
                                    className="filter-input"
                                    step="0.01"
                                />
                                {filter.operator === 'BETWEEN' && (
                                    <input
                                        type="number"
                                        value={filter.value2}
                                        onChange={(e) => updateFilter(filter.id, 'value2', e.target.value)}
                                        placeholder="До"
                                        className="filter-input"
                                        step="0.01"
                                    />
                                )}
                            </>
                        )}
                    </>
                );

            case 'application':
                return (
                    <select
                        value={filter.value}
                        onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                        className="filter-input"
                    >
                        <option value="">Оберіть застосування</option>
                        {applications.map(app => (
                            <option key={app.id} value={app.id}>{app.name}</option>
                        ))}
                    </select>
                );

            case 'element':
                return (
                    <>
                        <select
                            value={filter.value}
                            onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                            className="filter-input"
                        >
                            <option value="">Оберіть елемент</option>
                            {elements.map(elem => (
                                <option key={elem.id} value={elem.id}>
                                    {elem.symbol} - {elem.name}
                                </option>
                            ))}
                        </select>
                    </>
                );

            case 'name':
                return (
                    <input
                        type="text"
                        value={filter.value}
                        onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                        placeholder="Введіть назву"
                        className="filter-input"
                    />
                );

            default:
                return null;
        }
    };

    const getOperators = (filterType) => {
        if (filterType === 'density' || filterType === 'property') {
            return operators.numeric;
        } else if (filterType === 'name') {
            return operators.text;
        } else {
            return operators.select;
        }
    };

    return (
        <div className="advanced-search">
            <div className="search-header">
                <h2>🔬 Конструктор фільтрів</h2>
                <p className="search-description">
                    Створіть складні запити з комбінацією різних умов
                </p>
            </div>

            <div className="filter-builder">
                <div className="builder-controls">
                    <button onClick={addFilter} className="add-filter-btn">
                        ➕ Додати фільтр
                    </button>
                    
                    {filters.length > 1 && (
                        <div className="logic-selector">
                            <label>Логічний оператор:</label>
                            <select
                                value={logicOperator}
                                onChange={(e) => setLogicOperator(e.target.value)}
                                className="logic-select"
                            >
                                <option value="AND">І (AND) - всі умови</option>
                                <option value="OR">АБО (OR) - будь-яка умова</option>
                            </select>
                        </div>
                    )}
                </div>

                {filters.length > 0 && (
                    <div className="filters-list">
                        {filters.map((filter, index) => (
                            <div key={filter.id} className="filter-row">
                                {index > 0 && (
                                    <div className="logic-badge">
                                        {logicOperator}
                                    </div>
                                )}
                                
                                <select
                                    value={filter.type}
                                    onChange={(e) => updateFilter(filter.id, 'type', e.target.value)}
                                    className="filter-type"
                                >
                                    {filterTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>

                                {(filter.type === 'density' || filter.type === 'property' || filter.type === 'name') && (
                                    <select
                                        value={filter.operator}
                                        onChange={(e) => updateFilter(filter.id, 'operator', e.target.value)}
                                        className="filter-operator"
                                    >
                                        {getOperators(filter.type).map(op => (
                                            <option key={op.value} value={op.value}>
                                                {op.label}
                                            </option>
                                        ))}
                                    </select>
                                )}

                                <div className="filter-values">
                                    {renderFilterValue(filter)}
                                </div>

                                <button
                                    onClick={() => removeFilter(filter.id)}
                                    className="remove-filter-btn"
                                    title="Видалити фільтр"
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="search-actions">
                    <button
                        onClick={executeSearch}
                        className="execute-btn"
                        disabled={filters.length === 0}
                    >
                        🔍 Виконати пошук
                    </button>
                    <button
                        onClick={clearFilters}
                        className="clear-btn"
                        disabled={filters.length === 0}
                    >
                        🗑️ Очистити фільтри
                    </button>
                </div>
            </div>

            {results.length > 0 && (
                <ResultsDisplay 
                    results={results}
                    onMaterialSelect={setSelectedMaterial}
                />
            )}

            {filters.length > 0 && results.length === 0 && (
                <div className="no-results">
                    <div className="no-results-icon">🔍</div>
                    <p>Натисніть "Виконати пошук" щоб побачити результати</p>
                </div>
            )}
        </div>
    );
}

export default AdvancedSearch;
